
import React from 'react';
import { Input } from   '@/components/ui/InputDefault';
import { Textarea } from   '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from   '@/components/ui/DefaultSelectDropdown';
import TagSelector from   '@/components/ui/TagSelector';
import GlossaryItemSelector from   '@/components/ui/ObjectSelector';
import DialogSelector from   '@/components/ui/DialogSelector';
import DocumentSelector from   '@/components/ui/DocumentSelector';
import { TabbedFileUploaderComponent } from   '@/components/editor/TabbedFileUploaderComponent';
import { generateUUID, generateId } from '@/lib/utils/uuid';
import CustomCheckbox from   '@/components/ui/CustomCheckbox';

// Field type definitions
export interface FieldDefinition {
  type: 'text' | 'textarea' | 'number' | 'select' | 'file' | 'image' | 'url' | 'tags' | 'types' | 'glossaryItem' | 'dialog' | 'document' | 'boolean';
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  accept?: string;
  component: string; // The component to render this field
}

// Component registry - maps component names to actual React components
export const COMPONENT_REGISTRY: Record<string, React.ComponentType<any>> = {
  TextField: Input,
  TextArea: Textarea,
  NumberField: Input,
  CustomCheckbox: CustomCheckbox,
  SelectField: Select,
  FileUploader: TabbedFileUploaderComponent,
  ImageUploader: TabbedFileUploaderComponent,
  UrlField: Input,
  TagsField: TagSelector,
  TypesField: Select, // You might want to create a specific TypesField component
  GlossaryItemSelector: GlossaryItemSelector,
  DialogSelector: DialogSelector,
  DocumentSelector: DocumentSelector
};

// Available fields for blueprints and glossary items
export const AVAILABLE_FIELDS: Record<string, FieldDefinition> = {
  boolean: {
    type: 'boolean',
    label: 'Boolean',
    required: false,
    component: 'CustomCheckbox'
  },
  title: {
    type: 'text',
    label: 'Title',
    required: false,
    placeholder: 'Enter title',
    component: 'TextField'
  },
  textarea: {
    type: 'textarea',
    label: 'Text Area',
    required: false,
    placeholder: 'Enter text',
    component: 'TextArea'
  },
  number: {
    type: 'number',
    label: 'Number',
    required: false,
    placeholder: 'Enter number',
    component: 'NumberField'
  },
  file: {
    type: 'file',
    label: 'File',
    required: false,
    accept: '*/*',
    component: 'FileUploader'
  },
  image: {
    type: 'image',
    label: 'Image',
    required: false,
    accept: 'image/*',
    component: 'ImageUploader'
  },
  url: {
    type: 'url',
    label: 'URL',
    required: false,
    placeholder: 'Enter URL',
    component: 'UrlField'
  },
 /*  tags: {
    type: 'tags',
    label: 'Tags',
    required: false,
    component: 'TagsField'
  }, */
  types: {
    type: 'types',
    label: 'Types',
    required: false,
    component: 'TypesField'
  },
  glossaryItem: {
    type: 'glossaryItem',
    label: 'Library Object',
    required: false,
    component: 'GlossaryItemSelector'
  },
  dialog: {
    type: 'dialog',
    label: 'Dialog',
    required: false,
    component: 'DialogSelector'
  },
  document: {
    type: 'document',
    label: 'Document',
    required: false,
    component: 'DocumentSelector'
  }
};

// Function to get component for a field type
export function getFieldComponent(fieldType: string): string {
  const componentMap: Record<string, string> = {
    'text': 'TextField',
    'textarea': 'TextArea',
    'number': 'NumberField',
    'select': 'SelectField',
    'file': 'FileUploader',
    'image': 'ImageUploader',
    'url': 'UrlField',
    'tags': 'TagsField',
    'types': 'TypesField',
    'glossaryItem': 'GlossaryItemSelector',
    'dialog': 'DialogSelector',
    'document': 'DocumentSelector',
    'boolean': 'CustomCheckbox'
  };
  
  return componentMap[fieldType] || 'TextField';
}

// Function to get the actual React component from the registry
export function getComponentFromRegistry(componentName: string): React.ComponentType<any> | null {
  return COMPONENT_REGISTRY[componentName] || null;
}

// Function to render a field with its component
export function renderField(
  field: FieldDefinition, 
  value: any, 
  onChange: (value: any) => void,
  additionalProps: Record<string, any> = {}
): React.ReactElement | null {
  const Component = getComponentFromRegistry(field.component);
  
  if (!Component) {
    console.warn(`Component ${field.component} not found in registry`);
    return null;
  }

  // Prepare props based on field type
  const props: any = {
    ...additionalProps,
    value: value || '',
    onChange: onChange,
    placeholder: field.placeholder,
    required: field.required,
  };

  // Add type-specific props
  switch (field.type) {
    case 'boolean':
      props.checked = value;
      props.onChange = onChange;
      break;
    case 'number':
      props.type = 'number';
      break;
    case 'url':
      props.type = 'url';
      break;
    case 'file':
    case 'image':
      props.accept = field.accept;
      if (field.type === 'image') {
        props.onImageUpload = onChange;
      }
      break;
    case 'select':
      props.options = field.options;
      break;
  }

  return React.createElement(Component, props);
}

// Function to convert BlueprintData to form fields
export function blueprintDataToFormFields(blueprintData: any): FieldDefinition[] {
  const fields: FieldDefinition[] = [];
  
  if (blueprintData?.sections) {
    Object.entries(blueprintData.sections).forEach(([sectionName, sectionData]) => {
      if (typeof sectionData === 'object' && sectionData !== null) {
        // Handle nested fields
        Object.entries(sectionData).forEach(([fieldName, fieldConfig]) => {
          if (typeof fieldConfig === 'object' && fieldConfig !== null && 'type' in fieldConfig) {
            const config = fieldConfig as any;
            fields.push({
              type: config.type,
              label: fieldName,
              required: config.req || false,
              component: getFieldComponent(config.type)
            });
          }
        });
      }
    });
  }
  
  return fields;
}




// Function to convert blueprint type string to field key from AVAILABLE_FIELDS
export function blueprintTypeToFieldKey(blueprintType: string): string {
  // Direct mapping from blueprint types to field keys
  const typeMapping: Record<string, string> = {
    'title': 'title',
    'content': 'textarea', // Map content to textarea
    'textarea': 'textarea',
    'number': 'number',
    'boolean': 'boolean',
    'file': 'file',
    'image': 'image',
    'url': 'url',
    'tags': 'tags',
    'types': 'types',
    'glossaryItem': 'glossaryItem',
    'dialog': 'dialog',
    'document': 'document',
    'select': 'number', // Map select to number for now, or create a select field
    'text': 'title', // Map text to title
  };
  
  return typeMapping[blueprintType] || 'title';
}

